import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import StatusBadge from "../components/StatusBadge";
import AcceptProposal from "../components/AcceptProposal";
import { renderMarkdown } from "../lib/markdown";
import { generateQuotePdf } from "../lib/pdf";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Loader2, Download, FileText, Clock, AlertTriangle, CalendarCheck } from "lucide-react";

const mdy = (s) => { if (!s) return ""; const p = String(s).split("-"); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : s; };

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicQuote() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const action = params.get("action");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    api.get(`/public/quotes/by-token/${token}`)
      .then(({ data }) => setQuote(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  // Poll Stripe status after returning from checkout
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          const { data: q } = await api.get(`/public/quotes/by-token/${token}`);
          setQuote(q);
          setVerifying(false);
          toast.success("Deposit paid — your project is confirmed!");
          window.history.replaceState({}, "", `/q/${token}`);
          return;
        }
        if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
        if (attempts++ < 6) setTimeout(poll, 2000); else setVerifying(false);
      } catch {
        if (attempts++ < 6) setTimeout(poll, 2000); else setVerifying(false);
      }
    };
    poll();
  }, [params, token]);

  const depositAmount = quote
    ? (quote.deposit_amount_due ?? Math.round((quote.one_time_total ? quote.one_time_total * 0.3 : quote.monthly_total) * 100) / 100)
    : 0;
  const expired = !!(quote && quote.is_expired);
  const daysLeft = quote && quote.expires_at
    ? Math.ceil((new Date(quote.expires_at).getTime() - Date.now()) / 86400000)
    : null;

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/quotes/by-token/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-24 font-mono text-sm text-zinc-500">Loading your quote…</div>;
  if (notFound || !quote) return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="public-quote-notfound">
      <h1 className="font-display font-black text-3xl tracking-tighter">Quote not found</h1>
      <p className="mt-3 text-zinc-600">This link may have expired or is invalid. Please contact Brand Bloom for an updated quote.</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="public-quote-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display font-black text-4xl tracking-tighter">Quote #{quote.id.slice(-8).toUpperCase()}</h1>
            <StatusBadge status={quote.status} />
          </div>
          <p className="mt-2 text-zinc-600">Prepared for {quote.client_name}{quote.company ? ` · ${quote.company}` : ""}</p>
          {quote.expected_completion && (
            <p data-testid="public-expected-completion" className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-primary">
              <CalendarCheck className="h-3.5 w-3.5" /> Expected completion {mdy(quote.expected_completion)}
            </p>
          )}
          {!quote.deposit_paid && !quote.signed && (
            expired ? (
              <p data-testid="public-expiry-note" className="mt-2 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-red-600">
                <AlertTriangle className="h-3.5 w-3.5" /> Expired
              </p>
            ) : daysLeft != null && (
              <p data-testid="public-expiry-note" className="mt-2 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-amber-600">
                <Clock className="h-3.5 w-3.5" /> Expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}
              </p>
            )
          )}
        </div>
        <Button onClick={() => generateQuotePdf(quote)} data-testid="public-download-pdf-btn" variant="outline" className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-xs">
          <Download className="h-4 w-4 mr-1" /> Download PDF
        </Button>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-5 space-y-4">
          <div className="border border-zinc-900 bg-white shadow-[6px_6px_0px_0px_rgba(10,10,10,1)]">
            <div className="bg-zinc-900 text-white px-5 py-3 font-display font-bold">Quote Breakdown</div>
            <div className="p-5">
              <ul className="space-y-3">
                {quote.line_items.map((li, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span className="text-zinc-700">{li.name}</span>
                    <span className="font-mono">{fmt(li.price)}{li.billing === "monthly" ? "/mo" : ""}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-zinc-200 mt-4 pt-4 space-y-1">
                <div className="flex justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">One-time</span>
                  <span className="font-display font-black text-xl">{fmt(quote.one_time_total)}</span>
                </div>
                {quote.monthly_total > 0 && (
                  <div className="flex justify-between">
                    <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Monthly</span>
                    <span className="font-display font-bold text-lg text-primary">{fmt(quote.monthly_total)}/mo</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Accept & sign / Pay — or expired notice */}
          {expired ? (
            <div className="border border-red-300 bg-red-50 p-5" data-testid="public-expired-card">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <p className="font-display font-bold">This quote has expired</p>
              </div>
              <p className="mt-2 text-sm text-red-700/80">Quotes are valid for 10 days. Please reach out to Brand Bloom for a refreshed quote and we'll get you a new link.</p>
            </div>
          ) : (
            <>
              {/* Accept & sign */}
              <AcceptProposal
                quote={quote}
                submitUrl={`/public/quotes/by-token/${token}/accept`}
                autoOpen={action === "accept"}
                onAccepted={(q) => setQuote(q)}
              />

              {/* Pay deposit */}
              <div className="border border-zinc-900 bg-white p-5" data-testid="public-deposit-panel">
                {quote.deposit_paid ? (
                  <div className="flex items-center gap-3" data-testid="public-deposit-paid-state">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                    <div>
                      <p className="font-display font-bold">Deposit Paid</p>
                      <p className="font-mono text-xs text-zinc-500">{fmt(quote.deposit_amount || depositAmount)} received · project confirmed</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Deposit to start (30%)</p>
                        <p className="font-display font-black text-2xl tracking-tighter">{fmt(depositAmount)}</p>
                      </div>
                      <CreditCard className="h-7 w-7 text-primary" />
                    </div>
                    <Button
                      onClick={payDeposit}
                      disabled={paying || verifying}
                      data-testid="public-pay-deposit-btn"
                      className="w-full mt-4 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11"
                    >
                      {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : verifying ? "Verifying payment…" : "Pay Deposit Now"}
                    </Button>
                    <p className="mt-2 text-[11px] text-zinc-400 text-center">Secure checkout via Stripe. Remaining balance billed on delivery.</p>
                  </>
                )}
              </div>

              {/* Denefits financing option */}
              {quote.denefits?.hosted_checkout_url && !quote.deposit_paid && (
                <div className="border border-primary/30 bg-primary/5 p-5" data-testid="public-denefits-panel">
                  <p className="font-mono text-xs uppercase tracking-widest text-primary">Prefer to pay monthly?</p>
                  <p className="mt-1 font-display font-black text-xl tracking-tighter">~{fmt(quote.denefits.monthly_estimate)}/mo</p>
                  <p className="font-mono text-[11px] text-zinc-500">{fmt(quote.denefits.financed_amount)} financed over {quote.denefits.term_months} months</p>
                  <Button
                    onClick={() => window.open(quote.denefits.hosted_checkout_url, "_blank")}
                    data-testid="public-denefits-btn"
                    className="w-full mt-3 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11"
                  >
                    Finance with Denefits
                  </Button>
                  <p className="mt-2 text-[11px] text-zinc-400 text-center">Flexible monthly payments powered by Denefits.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Proposal */}
        <div className="lg:col-span-7">
          <div className="border border-zinc-200 bg-white">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-200">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="font-display font-bold text-lg">Your Proposal</h3>
            </div>
            <div className="p-6">
              {quote.proposal ? (
                <div
                  data-testid="public-proposal-content"
                  className="proposal-md text-sm text-zinc-800"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(quote.proposal) }}
                />
              ) : (
                <p className="py-10 text-center text-zinc-500">Your detailed proposal will appear here shortly.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
